/**
 * Staff session store (§17.1 admin auth, §3 RBAC). The session is `GET /v1/admin/me`; it refreshes on window
 * focus, clears on any 401 and exposes `can(permission)` built on `can()` from @windrop/contracts.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can as rbacCan, type Permission, type StaffSessionDto } from '@windrop/contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { identityApi } from '../lib/api/admin';
import { apiEvents, isApiError } from '../lib/api/client';

export const SESSION_QUERY_KEY = ['session'] as const;

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'error';

export interface SessionContextValue {
  session: StaffSessionDto | null;
  status: SessionStatus;
  /** Permission check driven by `can()` from contracts (super-admin needs an active JIT grant). */
  can: (permission: Permission) => boolean;
  /** True while the step-up window (`stepUpUntil`) is in the future. */
  stepUpActive: boolean;
  setSession: (session: StaffSessionDto | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const isJitActive = (session: StaffSessionDto | null, now = Date.now()): boolean =>
  Boolean(session?.jitGrant && Date.parse(session.jitGrant.grantedUntil) > now);

export const isStepUpActive = (session: StaffSessionDto | null, now = Date.now()): boolean =>
  Boolean(session?.stepUpUntil && Date.parse(session.stepUpUntil) > now);

export const sessionCan = (session: StaffSessionDto | null, permission: Permission): boolean =>
  session ? rbacCan(session.roles, permission, { superAdminJitActive: isJitActive(session) }) : false;

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<StaffSessionDto | null> => {
      try {
        return await identityApi.me();
      } catch (e) {
        if (isApiError(e) && (e.status === 401 || e.status === 403)) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const setSession = useCallback(
    (session: StaffSessionDto | null) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
    },
    [queryClient],
  );

  useEffect(() => {
    return apiEvents.subscribe((event) => {
      if (event === 'unauthenticated') {
        queryClient.setQueryData(SESSION_QUERY_KEY, null);
      }
    });
  }, [queryClient]);

  const session = query.data ?? null;
  const status: SessionStatus = query.isPending
    ? 'loading'
    : query.isError
      ? 'error'
      : session
        ? 'authenticated'
        : 'anonymous';

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      status,
      can: (permission) => sessionCan(session, permission),
      stepUpActive: isStepUpActive(session),
      setSession,
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      },
      logout: async () => {
        try {
          await identityApi.logout();
        } finally {
          queryClient.setQueryData(SESSION_QUERY_KEY, null);
          queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' });
        }
      },
    }),
    [session, status, setSession, queryClient],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** Test/storybook helper: a static session without network. */
export function StaticSessionProvider({
  session,
  children,
}: {
  session: StaffSessionDto | null;
  children: ReactNode;
}) {
  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      status: session ? 'authenticated' : 'anonymous',
      can: (permission) => sessionCan(session, permission),
      stepUpActive: isStepUpActive(session),
      setSession: () => {},
      refresh: async () => {},
      logout: async () => {},
    }),
    [session],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
